import { type DependencyList, useEffect, useId, useLayoutEffect, useRef } from "react"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"
import { useSearchCommandsStore } from "~/stores/search-commands-store"
import { findCommand } from "./pageCommands"
import type { PageCommand } from "./types"

type CommandsRef = { readonly current: readonly PageCommand[] }

const withLatestRun = (
  commands: readonly PageCommand[],
  latest: CommandsRef,
  parentPath: readonly string[] = [],
): PageCommand[] =>
  commands.map((command) => {
    const path = [...parentPath, command.id]
    if (command.subCommands) {
      return { ...command, subCommands: withLatestRun(command.subCommands, latest, path) }
    }
    return { ...command, run: () => findCommand(latest.current, path)?.run?.() }
  })

/**
 * Shows `commands` in the command menu's "This page" group while the caller is mounted.
 * `deps` work like `useMemo`'s: list what the names, permissions or sub-lists depend on; `run`
 * always sees the latest render.
 */
export function useRegisterSearchCommands(commands: readonly PageCommand[], deps: DependencyList) {
  const key = useId()
  const covered = useIsCoveredByFullPageDialog()
  const register = useSearchCommandsStore((s) => s.register)
  const unregister = useSearchCommandsStore((s) => s.unregister)
  const latest = useRef(commands)

  useLayoutEffect(() => {
    latest.current = commands
  })

  useEffect(() => {
    register(key, { commands: withLatestRun(latest.current, latest), covered })
  }, [key, covered, register, ...deps])

  useEffect(() => () => unregister(key), [key, unregister])
}
